import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import type { Map } from 'maplibre-gl';
import { useCallback, useEffect, useState } from 'react';

import { getMapStyle } from '@/endpoints/mapbox/getMapStyle';
import { selectedBaseMapKey } from '@/features/embed/mapSettingsComponents/embedBaseMap';
import type { ReactSetState } from '@/models/reactTypes';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { priorityLayers } from '@/utils/addLayer';
import { isLayerLoaded } from '@/utils/asyncMapbox';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

export enum MapStyle {
  SatelliteStreets = '08a71ab3-1578-4831-9069-94978866b9e5', // custom satellite-streets
  Satellite = '91a7c4fa-40bc-4aa8-af6c-e8368ca52351', // custom satellite
  Streets = 'acb9530e-53b4-488a-89f4-708b22a12bc3',
  Topography = 'b08b3220-fffb-45f8-9b80-f0dd932449ce',
  Light = 'dataviz-light',
  Dark = 'dataviz-dark',
}

export const baseMapImgMap = {
  [MapStyle.SatelliteStreets]: '/img/base-map/satellite-streets.webp',
  [MapStyle.Satellite]: '/img/base-map/satellite.webp',
  [MapStyle.Streets]: '/img/base-map/streets.webp',
  [MapStyle.Topography]: '/img/base-map/topography.webp',
  [MapStyle.Light]: '/img/base-map/light.webp',
  [MapStyle.Dark]: '/img/base-map/dark.webp',
};

export const mapStyleOptions = [
  {
    imgLink: baseMapImgMap[MapStyle.SatelliteStreets],
    label: 'Satellite Streets',
    value: MapStyle.SatelliteStreets,
  },
  {
    imgLink: baseMapImgMap[MapStyle.Satellite],
    label: 'Satellite',
    value: MapStyle.Satellite,
  },
  {
    imgLink: baseMapImgMap[MapStyle.Streets],
    label: 'Streets',
    value: MapStyle.Streets,
  },
  {
    imgLink: baseMapImgMap[MapStyle.Topography],
    label: 'Topography',
    value: MapStyle.Topography,
  },
  {
    imgLink: baseMapImgMap[MapStyle.Light],
    label: 'Light',
    value: MapStyle.Light,
  },
  {
    imgLink: baseMapImgMap[MapStyle.Dark],
    label: 'Dark',
    value: MapStyle.Dark,
  },
];

export type MapboxBaseMapState = {
  mapboxBaseMap: MapStyle;
  setMapboxBaseMap: (baseMap: MapStyle) => void;
};

export const emptyMapboxBaseMapState: MapboxBaseMapState = {
  mapboxBaseMap: MapStyle.SatelliteStreets,
  setMapboxBaseMap: unimplementedFunction,
};

export const useMapboxBaseMapState = (
  mapInstance: Map | undefined,
  isMapInstanceLoaded: boolean,
  setBaseMapLayersLoaded: ReactSetState<boolean>
): MapboxBaseMapState => {
  const { track } = useAnalytics();

  const [mapboxBaseMap, setMapboxBaseMap] = useState<MapStyle>(
    MapStyle.SatelliteStreets
  );

  // TODO: When using react-map-gl if the layers and sources are defined as children to the map with JSX elements
  //  then the layers get added auotmatically when the base map style changes, this is a big refactor for now but
  //  we should be moving in this direction very soon

  const handleStyleLoaded = useCallback(async () => {
    if (!mapInstance || !isMapInstanceLoaded || !mapboxBaseMap) return;

    const selectedMapOptionStyle = await getMapStyle(mapboxBaseMap);

    const allLayersLoaded = selectedMapOptionStyle.layers.map(async (layer) => {
      const layerHasLoaded = await isLayerLoaded(mapInstance, layer.id);

      return layerHasLoaded;
    });

    const allLayersLoadedRes = await Promise.all(allLayersLoaded);

    if (allLayersLoadedRes.includes(false)) return;

    priorityLayers.forEach((layer) => {
      if (mapInstance.getLayer(layer)) {
        mapInstance.moveLayer(layer);
      }
    });

    setBaseMapLayersLoaded(true);
  }, [isMapInstanceLoaded, mapInstance, mapboxBaseMap, setBaseMapLayersLoaded]);

  const handleChangeStyle = useCallback(
    async (selectedMapStyle: MapStyle) => {
      if (!mapInstance) return;
      setBaseMapLayersLoaded(false);

      localStorage.setItem(selectedBaseMapKey, selectedMapStyle);
      setMapboxBaseMap(selectedMapStyle);
      mapInstance.once('styledata', handleStyleLoaded);

      priorityLayers.forEach((layer) => {
        if (mapInstance.getLayer(layer)) {
          mapInstance.moveLayer(layer);
        }
      });
    },
    [handleStyleLoaded, mapInstance, setBaseMapLayersLoaded]
  );

  useEffect(() => {
    if (!mapInstance) return;

    const selectedStyleStorage = localStorage.getItem(selectedBaseMapKey);

    if (selectedStyleStorage) {
      setMapboxBaseMap(selectedStyleStorage as MapStyle);
      handleChangeStyle(selectedStyleStorage as MapStyle);
    }
  }, [handleChangeStyle, mapInstance]);

  const trackedMapboxBaseMapSetter = useCallback(
    (baseMap: MapStyle) => {
      handleChangeStyle(baseMap);
      track({
        data: {
          from: mapboxBaseMap,
          to: baseMap,
        },
        event: TrackingEvent.BASE_MAP_STYLE_CHANGED,
      });
    },
    [handleChangeStyle, mapboxBaseMap, track]
  );

  return {
    mapboxBaseMap,
    setMapboxBaseMap: trackedMapboxBaseMapSetter,
  };
};
